import { useState } from 'react';
import { contractSchema, type Project } from '@forecastify/shared';
import { Empty } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { useWorkspaceNavigate } from '../../workspace-path';
import { parseList, usePermissionSet } from '../permissions-hook';
import { contractLabel } from '../contracts/contract-label';

/**
 * The tabs a project without a line item still opens: the two that fix it, the one that says
 * what is wrong with it (P2-32.4), and its history — what happened to a project does not wait for
 * it to be able to hold work (ux-pass-2).
 */
export const UNGATED_PROJECT_TABS: ReadonlySet<string> = new Set([
  'contracts',
  'people',
  'alerts',
  'history',
]);

/** Whether a project can hold work yet. `0164` refuses a task without a line item. */
export function projectIsUsable(project: Pick<Project, 'lineItemCount'>): boolean {
  return project.lineItemCount > 0;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ P2-32.3 · the project gate — a start state, not an empty one.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE BLOCK IN PLACE OF THE TAB, NOT A BANNER ABOVE IT.** Every task needs a line item now,
 * so a project without one has nothing to show on any of these tabs, and drawing them empty under a
 * warning is the *six empty boxes* `state-rules.md` §4 says start states exist to avoid.
 *
 * ⚠️ **It names the missing step and offers that step's action, and only that one:** *Új szerződés*
 * opens the contract dialog on the Szerződések tab; *Új sortétel* opens the line item dialog on the
 * contract when there is exactly one, and the tab when there are several, because picking which
 * contract is the decision the person came to make. Without the permission the action is absent and
 * the copy says who can do it — a button that will be refused is worse than none.
 *
 * Not drawn by the mockup; the shape is `DataList`'s empty block (`Empty`), as `line-item-sync.md`
 * §5 says.
 */
export function ProjectStartState({
  project,
  onNewContract,
  onPickContract,
}: {
  readonly project: Project;
  /** Opens the Szerződések tab with its create dialog open. */
  readonly onNewContract: () => void;
  /** Opens the Szerződések tab, for a project with several contracts to choose between. */
  readonly onPickContract: () => void;
}) {
  const t = useT();
  const session = useSession();
  const go = useWorkspaceNavigate();
  const permissions = usePermissionSet();
  const [busy, setBusy] = useState(false);

  const hasContract = project.contractCount > 0;
  const allowed = permissions.has(hasContract ? 'lineitem.manage' : 'contract.manage');

  /* ⚠️ Read when clicked, not on mount: the gate is drawn on every tab of every new project, and
     the contract it needs is only worth a request once somebody asks for it. */
  const newLineItem = (): void => {
    if (project.contractCount > 1) {
      onPickContract();
      return;
    }
    setBusy(true);
    void (async () => {
      const response = await session
        .request(`/contracts?projectId=${project.id}`)
        .catch(() => null);
      setBusy(false);
      const contract =
        response?.ok === true
          ? (parseList(contractSchema, await response.json())?.find(
              (candidate) => candidate.projectId === project.id,
            ) ?? null)
          : null;
      if (contract === null) {
        onPickContract();
        return;
      }
      go(`/contracts/${contractLabel(contract)}?newLineItem=1`);
    })();
  };

  return (
    <div data-gate="project" className="flex min-h-0 flex-1 flex-col items-center justify-center">
      <Empty
        icon="file-text"
        title={t('project.gate.title')}
        body={t(allowed ? 'project.gate.body' : 'project.gate.bodyNoAccess')}
        steps={[
          { label: t('project.gate.step.contract'), done: hasContract },
          { label: t('project.gate.step.lineItem'), done: false },
        ]}
        {...(allowed && !busy
          ? {
              actionLabel: t(hasContract ? 'contract.lineItem.new' : 'contract.action.new'),
              onAction: hasContract ? newLineItem : onNewContract,
            }
          : {})}
      />
    </div>
  );
}
